import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import * as QRCode from 'qrcode';
import httpService from '../../services/httpService';
import Button from '../misc/Button';
import Modal from '../layouts/Modal';

interface ShareAccountModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export default function ShareAccountModal({ open, setOpen }: ShareAccountModalProps): JSX.Element {
  const { t } = useTranslation();
  const [personalLink, setPersonalLink] = useState<string>('');
  const [qrCode, setQrCode] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const buildLink = (friendlyId?: string) => `${window.location.origin}/vc/${friendlyId}`;

  const generateQRCode = async (link: string) => {
    try {
      const qr = await QRCode.toDataURL(link);
      setQrCode(qr);
    } catch (e) {
      setQrCode('');
    }
  };

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const fetchOrGenerate = async () => {
      try {
        const { data } = await httpService.get('/personal-links/@me');
        let invite = data?.invite;
        if (!invite) {
          const { data: gen } = await httpService.post('/personal-links/@me');
          invite = gen?.invite;
        }

        if (invite) {
          const link = buildLink(invite);
          setPersonalLink(link);
          await generateQRCode(link);
        } else {
          setPersonalLink('');
          setQrCode('');
        }
      } catch (error) {
        setPersonalLink('');
        setQrCode('');
      } finally {
        setLoading(false);
      }
    };
    fetchOrGenerate();
  }, [open]);

  const copyLink = async () => {
    if (!personalLink) return;
    await navigator.clipboard.writeText(personalLink);
    toast.success(t('toast.success.copied'));
    setOpen(false);
  };

  const regenerateLink = async () => {
    setLoading(true);
    try {
      const { data } = await httpService.post('/personal-links/@me');
      if (data?.invite) {
        const link = buildLink(data.invite);
        setPersonalLink(link);
        await generateQRCode(link);
        toast.success(t('toast.success.regenerated'));
      }
    } catch (e) {
      toast.error(t('toast.error.general'));
    } finally {
      setLoading(false);
    }
  };

  const footer = (
    <div className="ml-auto flex gap-2">
      <Button className="underline" onClick={() => setOpen(false)}>
        {t('general.cancel')}
      </Button>
    </div>
  );

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title={t('page.profile.shareTitle')}
      footer={footer}
      className="flex w-full flex-col gap-4">
      <p className="text-secondary-500 text-sm">{t('page.profile.shareDescription')}</p>

      {qrCode && (
        <div className="mt-2 flex flex-col items-center">
          <img src={qrCode} alt={t('page.profile.qrCode')} className="w-36 rounded-lg" />
        </div>
      )}

      <div className="mt-4 flex w-full items-center gap-2">
        <input className="flex-1 rounded-full border !bg-secondary-50 px-4 text-sm" value={personalLink} readOnly />
        <Button variant="primary" onClick={copyLink} disabled={!personalLink}>
          {t('page.profile.copyLink')}
        </Button>
      </div>

      <div>
        <Button variant="primary" onClick={regenerateLink} disabled={loading}>
          {t('page.profile.regenerateLink')}
        </Button>
      </div>
    </Modal>
  );
}
